import { useForm } from 'react-hook-form';
import { ChangeEvent, useEffect } from 'react';
import { MembershipFeePageContentResponseModel } from '@shared/integrations/umbraco-taf-delivery-api/models';
import { IPerson } from '@shared/integrations/taf-api/v2/models';
import {
    getBankByClearingNumber,
    getBankNameByClearingNumber,
    validateAccountNumberAndClearingNumber
} from '@shared/_legacy/services/AutogiroService.tsx';
import { useToast } from '@shared/features/toasts/contexts/ToastProvider';
import Alerts from '@shared/components/Alerts.tsx';
import useEmploymentFormActions from '@my-pages/features/employment/hooks/useEmploymentFormActions.ts';

interface IMemberFeeFormProps {
    member: IPerson;
    content: MembershipFeePageContentResponseModel;

    closeForm: () => void;
}

const MemberFeeForm = ({ content, member, closeForm }: IMemberFeeFormProps) => {
    const { dangerToast } = useToast();

    const {
        handleSubmit,
        register,
        formState: { isValid, isSubmitting },
        setValue,
        getValues,
        watch,
    } = useForm({ mode: 'onChange' });

    const onClearingNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
        setValue('bank', getBankNameByClearingNumber(e.target.value));
    };

    const { createFederalCase } = useEmploymentFormActions();

    useEffect(() => {
        register('clearingNumber', { onChange: onClearingNumberChange });
        watch('clearingNumber');
    }, []);

    const submit = async (data: any) => {
        if (!member.socialSecurityNumber) {
            dangerToast('Person nummer kunde inte verifieras');
            return;
        }
        const accountNumber = data.clearingNumber + data.accountNumber;
        const success = await createFederalCase({ accountNumber, socialSecurityNumber: member.socialSecurityNumber });
        if (success) closeForm();
    };
    return (
        <>
            <h1 className='page-title'>Redigera betalsätt</h1>
            <div className='slide-up-fade-in'>
                {content?.properties?.importantMessages && (
                    <div className='mb-4'>
                        <Alerts alerts={content?.properties?.importantMessages} />
                    </div>
                )}
                <div className='card card-body mb-4'>
                    <div className='row'>
                        <div className='col-12'>
                            <h3>Betalsätt</h3>
                            <form
                                onSubmit={handleSubmit(submit)}
                                className='fade-in-medium'
                            >
                                <p className='required-info'>* Obligatoriska uppgifter</p>
                                <div className={getValues('bank') ? ' slide-up-fade-in' : ' slide-down-fade-out h-0'}>
                                    <div className='form-group row'>
                                        <label
                                            htmlFor='bank'
                                            className='col-md-3 col-form-label label-sm'
                                        >
                                            <span className='d-none'>Bank</span>
                                        </label>
                                        <div className='col-md-9'>
                                            <input
                                                {...register('bank')}
                                                disabled={true}
                                                type='text'
                                                className='highlight-ignore form-control form-control-lg form-control-plaintext font-italic bg-white'
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className='form-group row'>
                                    <label
                                        htmlFor='clearingNumber'
                                        className='col-md-3 col-form-label label-sm label-required'
                                    >
                                        Clearingnummer
                                    </label>
                                    <div className='col-md-9'>
                                        <input
                                            {...register('clearingNumber', { required: true })}
                                            type='number'
                                            id='clearingNumber'
                                            name='clearingNumber'
                                            className='form-control highlight-ignore'
                                        />
                                        <ul className='form-text text-muted font-size-sm mb-0'>
                                            <li>Skriv in de 4 första siffrorna i clearingnumret</li>
                                        </ul>
                                    </div>
                                </div>
                                <div className={getValues('bank') ? ' slide-up-fade-in' : ' slide-down-fade-out h-0'}>
                                    <div className='form-group row'>
                                        <label
                                            htmlFor='accountNumber'
                                            className='col-md-3 col-form-label label-sm label-required'
                                        >
                                            Kontonummer
                                        </label>
                                        <div className='col-md-9'>
                                            <input
                                                {...register('accountNumber', {
                                                    required: true,
                                                    validate: (accountNo) => validateAccountNumberAndClearingNumber(getValues('clearingNumber'), accountNo),
                                                })}
                                                type='number'
                                                id='accountNumber'
                                                disabled={!getValues('bank')}
                                                className='form-control highlight-ignore'
                                            />
                                            <ul className='form-text text-muted font-size-sm mb-0'>
                                                <li>
                                                    {getBankNameByClearingNumber(getValues('clearingNumber'))} tillåter högst {getBankByClearingNumber(getValues('clearingNumber'))?.accountNumberMax} siffror i kontonummer
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                <div className='d-flex justify-content-sm-end mt-5 mb-4'>
                                    <button
                                        className='btn btn-light btn-lg mr-3'
                                        type='button'
                                        onClick={() => closeForm()}
                                    >
                                        Avbryt
                                    </button>
                                    <button
                                        className='btn btn-primary btn-lg'
                                        disabled={!isValid || isSubmitting}
                                    >
                                        Spara
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default MemberFeeForm;
